import React from 'react'
import ReactDOM from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import './index.css'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { TradeSettingsProvider } from './contexts/TradeSettingsContext'
import { registerSW } from './utils/pwaUpdate'

const helmetContext = {};

// Détection de l'environnement de développement
const isDevelopment = () => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.port === '3000' || 
         window.location.port === '3001';
};

// Vérifier si nous avons un paramètre cache_buster dans l'URL
// Si oui, le supprimer pour éviter de polluer l'historique
const handleCacheBuster = () => {
  const url = new URL(window.location.href);
  if (url.searchParams.has('cache_buster')) {
    console.log('Cache buster détecté, nettoyage de l\'URL');
    url.searchParams.delete('cache_buster');
    window.history.replaceState({}, document.title, url.toString());
  }
};

// Nettoyer immédiatement l'URL si nécessaire
handleCacheBuster();

// Enregistrement du service worker avec gestion des mises à jour
const registerServiceWorker = async () => {
  try {
    if (isDevelopment()) {
      console.log('PWA: Mode développement - Service worker désactivé');
      
      // Simuler l'événement beforeinstallprompt pour tester l'UI d'installation
      if (window.location.search.includes('simulate-install')) {
        setTimeout(() => {
          const mockInstallEvent = new Event('beforeinstallprompt');
          // Empêcher la propagation par défaut
          mockInstallEvent.preventDefault = () => {};
          // Ajouter une méthode prompt simulée
          // @ts-expect-error - Ajout de la méthode prompt au mock event
          mockInstallEvent.prompt = async () => {
            console.log('PWA DEV: Simulation du prompt d\'installation');
            return { outcome: 'accepted' };
          };
          window.dispatchEvent(mockInstallEvent);
          
          // Stocker l'événement pour le déclencher plus tard
          console.log('L\'application est installable sur cet appareil (simulation)');
          
          // @ts-expect-error - L'événement deferredPrompt n'est pas dans les types standard
          window.deferredPrompt = mockInstallEvent;
        }, 3000);
      }
      return; // Arrêter ici en mode développement
    }
    
    console.log('PWA: Initialisation du service worker en production');
    
    // Enregistrer le service worker principal pour la PWA (uniquement en production)
    await registerSW();
    
    // Vérifier si la PWA est installable
    window.addEventListener('beforeinstallprompt', (e) => {
      // Empêcher Chrome 67+ de déclencher automatiquement le prompt
      e.preventDefault();
      
      // Stocker l'événement pour le déclencher plus tard
      console.log('L\'application est installable sur cet appareil');
      
      // Pour Android, nous pouvons stocker l'événement dans une variable globale
      // @ts-expect-error - L'événement deferredPrompt n'est pas dans les types standard
      window.deferredPrompt = e;
      
      // Afficher un bouton d'installation personnalisé si nécessaire
      const installButton = document.getElementById('install-button');
      if (installButton) {
        installButton.style.display = 'block';
      }
    });
    
    // Détecter quand l'application est installée
    window.addEventListener('appinstalled', () => {
      console.log('Application installée avec succès');
      // @ts-expect-error - L'événement deferredPrompt n'est pas dans les types standard
      window.deferredPrompt = null;
    });
  } catch (error) {
    console.error('Erreur lors de l\'initialisation de la PWA:', error);
    
    // En mode développement, ne pas montrer d'erreur pour les problèmes de service worker
    if (isDevelopment()) {
      console.info('En développement, les erreurs de service worker sont normales et n\'affectent pas le fonctionnement de l\'application');
    }
  }
};

// Nettoyer les anciennes versions du localStorage au démarrage
const cleanupOldVersions = () => {
  const currentTimestamp = Date.now();
  const lastCleanup = localStorage.getItem('lastVersionCleanup');
  
  // Ne nettoyer qu'une fois par jour
  if (!lastCleanup || (currentTimestamp - parseInt(lastCleanup)) > 24 * 60 * 60 * 1000) {
    localStorage.removeItem('lastNotifiedVersion');
    localStorage.setItem('lastVersionCleanup', currentTimestamp.toString());
  }
};

// Initialiser le service worker et nettoyer le stockage
cleanupOldVersions();
registerServiceWorker();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <TradeSettingsProvider>
        <HelmetProvider context={helmetContext}>
          <App />
        </HelmetProvider>
      </TradeSettingsProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
